// Save what you choose, how you choose it.
//
// From a logged meal's bookmark: pick which of its items to keep, then either
// bundle them into one named group (re-logs together) or save each as its own
// single food (re-logs individually). Owner request — saving was
// all-or-nothing before, and always as a group.
import { useState } from "react";
import { api, ApiError } from "../lib/api";
import { useT } from "../lib/i18n";
import { useToast } from "../lib/toast";
import { hapticSuccess } from "../lib/haptics";
import { SheetShell } from "./SheetShell";
import { Button } from "./ui/Button";

/** One selectable line — the fields a saved_meal_item needs. */
export interface SaveCandidate {
  name: string;
  kcal: number;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  sugar_g: number | null;
  micros_json: string | null;
  grams: number | null;
  unit: string | null;
}

interface Props {
  items: SaveCandidate[];
  /** Prefill for the group name (the meal slot label). */
  defaultName: string;
  onClose: () => void;
  onPaywall: () => void;
}

export function SaveItemsSheet({ items, defaultName, onClose, onPaywall }: Props) {
  const t = useT();
  const toast = useToast();
  const [picked, setPicked] = useState<Set<number>>(() => new Set(items.map((_, i) => i)));
  const [mode, setMode] = useState<"group" | "single">("group");
  const [name, setName] = useState(defaultName);
  const [busy, setBusy] = useState(false);

  const chosen = items.filter((_, i) => picked.has(i));
  const canSave = chosen.length > 0 && (mode === "single" || name.trim().length > 0);

  function toggle(i: number) {
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  }

  async function save() {
    if (!canSave || busy) return;
    setBusy(true);
    try {
      if (mode === "group") {
        await api.createSavedMeal(name.trim().slice(0, 120), null, chosen);
      } else {
        // Each selected item becomes its own one-item saved food.
        for (const it of chosen) {
          await api.createSavedMeal(it.name.slice(0, 120), null, [it]);
        }
      }
      hapticSuccess();
      toast.success(t("saved.saved_toast"));
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.message === "premium_required") { onClose(); onPaywall(); }
      else toast.error(t("toast.failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <SheetShell onClose={onClose}>
      <div className="space-y-4 pb-2">
        <h2 className="text-lg font-semibold">{t("saveitems.title")}</h2>

        {/* Which items */}
        <div className="space-y-1">
          {items.map((it, i) => (
            <button
              key={i}
              type="button"
              onClick={() => toggle(i)}
              className="w-full flex items-center gap-3 py-2 text-left press"
            >
              <span
                aria-hidden="true"
                className={`w-5 h-5 rounded-md border flex items-center justify-center text-[0.7rem] leading-none shrink-0 ${
                  picked.has(i) ? "bg-brand border-brand text-on-brand" : "border-line text-transparent"
                }`}
              >✓</span>
              <span className="flex-1 min-w-0 truncate text-sm">{it.name}</span>
              <span className="text-sm text-fg-muted tabular-nums">{it.kcal} kcal</span>
            </button>
          ))}
        </div>

        {/* How to save */}
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => setMode("group")}
            className={`flex-1 py-2.5 rounded-xl text-sm font-medium press ${
              mode === "group" ? "bg-brand text-on-brand" : "bg-surface text-fg-secondary"
            }`}
          >
            {t("saveitems.as_group")}
          </button>
          <button
            type="button"
            onClick={() => setMode("single")}
            className={`flex-1 py-2.5 rounded-xl text-sm font-medium press ${
              mode === "single" ? "bg-brand text-on-brand" : "bg-surface text-fg-secondary"
            }`}
          >
            {t("saveitems.as_single")}
          </button>
        </div>

        {mode === "group" ? (
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 120))}
            placeholder={t("saved.name_placeholder")}
            aria-label={t("saved.name_title")}
          />
        ) : (
          <p className="text-sm text-fg-muted">{t("saveitems.single_hint", { n: String(chosen.length) })}</p>
        )}

        <Button fullWidth disabled={!canSave || busy} onClick={save}>
          {mode === "group" ? t("more_goals.save") : t("saveitems.save_n", { n: String(chosen.length) })}
        </Button>
      </div>
    </SheetShell>
  );
}
